import React, { useMemo, useState, forwardRef, useImperativeHandle } from 'react';
import { Button } from '@nextui-org/react';
import { Settlement } from 'common/src/utils/types';
import { formatMoneyUSD } from '../../utils/functions';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';
import { Icon } from '@iconify/react';
import { TH_BASE_CLASS, TableExportRef } from '../../utils/table-styles';
import { exportTableToExcel } from '../../utils/export-to-excel';

interface Props {
  settlements: Settlement[];
}

type SortDir = 'asc' | 'desc';

const ExchangeRatesTable = forwardRef<TableExportRef, Props>(({ settlements }, ref) => {
  const { t } = useTranslation('settlements');
  // 'default' sentinel = sort by total FOB per client desc (biggest first).
  // Switches to a specific column once the user clicks a header.
  const [sortKey, setSortKey] = useState<string>('default');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [visibleCount, setVisibleCount] = useState(10);

  // Total FOB per client — used only for the default row order, not rendered.
  const fobByClient = useMemo(() => {
    const map = new Map<string, number>();
    settlements.forEach((s) => {
      const key = s.receiver || t('Sin Cliente');
      const fob = (s.settlement_details || []).reduce(
        (sum, d) => sum + (d.usd_total_fob || 0),
        0
      );
      map.set(key, (map.get(key) || 0) + fob);
    });
    return map;
  }, [settlements, t]);

  const { currencies, clientRows, averageRow } = useMemo(() => {
    const currencySet = new Set<string>();
    // client -> currency -> { weightedSum, totalSales }
    const data: Record<string, Record<string, { weightedSum: number; totalSales: number }>> = {};
    // currency -> { weightedSum, totalSales } for averages
    const totals: Record<string, { weightedSum: number; totalSales: number }> = {};
    // currency -> total FOB (USD). Used to order the column set desc by FOB.
    const fobByCurrency: Record<string, number> = {};

    settlements.forEach((s) => {
      if (!s.exchange_rate || !s.local_currency) return;
      const client = s.receiver || t('Sin Cliente');
      const currency = s.local_currency;
      currencySet.add(currency);

      const salesValue = (s.total_revenue_local || 0) - (s.total_commission_local || 0);

      if (!data[client]) data[client] = {};
      if (!data[client][currency]) data[client][currency] = { weightedSum: 0, totalSales: 0 };
      data[client][currency].weightedSum += s.exchange_rate * salesValue;
      data[client][currency].totalSales += salesValue;

      if (!totals[currency]) totals[currency] = { weightedSum: 0, totalSales: 0 };
      totals[currency].weightedSum += s.exchange_rate * salesValue;
      totals[currency].totalSales += salesValue;

      const fob = (s.settlement_details || []).reduce(
        (sum, d) => sum + (d.usd_total_fob || 0),
        0
      );
      fobByCurrency[currency] = (fobByCurrency[currency] || 0) + fob;
    });

    const currencies = Array.from(currencySet).sort(
      (a, b) => (fobByCurrency[b] || 0) - (fobByCurrency[a] || 0)
    );

    const clientRows = Object.entries(data).map(([client, currencyMap]) => {
      const rates: Record<string, number | null> = {};
      currencies.forEach((c) => {
        const d = currencyMap[c];
        rates[c] = d && d.totalSales > 0 ? d.weightedSum / d.totalSales : null;
      });
      return { client, rates };
    });

    const avgRates: Record<string, number | null> = {};
    currencies.forEach((c) => {
      const d = totals[c];
      avgRates[c] = d && d.totalSales > 0 ? d.weightedSum / d.totalSales : null;
    });
    const averageRow = { client: t('PROMEDIO'), rates: avgRates };

    return { currencies, clientRows, averageRow };
  }, [settlements, t]);

  const sortedClientRows = useMemo(() => {
    return [...clientRows].sort((a, b) => {
      let cmp: number;
      if (sortKey === 'default') {
        cmp =
          (fobByClient.get(a.client) || 0) -
          (fobByClient.get(b.client) || 0);
      } else if (sortKey === 'client') {
        cmp = a.client.localeCompare(b.client);
      } else {
        const aVal = a.rates[sortKey] ?? -Infinity;
        const bVal = b.rates[sortKey] ?? -Infinity;
        cmp = aVal - bVal;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [clientRows, sortKey, sortDir, fobByClient]);

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'client' ? 'asc' : 'desc');
    }
  };

  const SortIcon = ({ column }: { column: string }) => {
    if (sortKey === column) {
      return sortDir === 'asc' ? (
        <Icon icon='tabler:chevron-up' className='inline ml-1' width={12} />
      ) : (
        <Icon icon='tabler:chevron-down' className='inline ml-1' width={12} />
      );
    }
    return <Icon icon='tabler:chevron-down' className='inline ml-1 opacity-0 group-hover/th:opacity-40' width={12} />;
  };

  const fmtRate = (v: number | null) => (v !== null ? formatMoneyUSD(v, { decimals: 2 }) : '—');

  const handleExport = () => {
    const allRows = [averageRow, ...sortedClientRows];
    exportTableToExcel({
      fileName: 'Tipos_de_Cambio',
      sheetName: t('Tipos de Cambio'),
      headers: [t('CLIENTE'), ...currencies],
      rows: allRows.map((row) => [
        row.client,
        ...currencies.map((c) => fmtRate(row.rates[c])),
      ]),
    });
  };

  useImperativeHandle(ref, () => ({ exportToExcel: handleExport }), [handleExport]);

  if (currencies.length === 0) return null;

  return (
    <div>
      <div className='overflow-x-auto'>
      <table className='w-full text-sm'>
        <thead>
          <tr>
            <th
              className={`${TH_BASE_CLASS} bg-default-100 text-left rounded-tl-md`}
              onClick={() => handleSort('client')}
            >
              {t('CLIENTE')}
              <SortIcon column='client' />
            </th>
            {currencies.map((c, i) => (
              <th
                key={c}
                className={`${TH_BASE_CLASS} bg-default-100 text-right ${
                  i === currencies.length - 1 ? 'rounded-tr-md' : ''
                }`}
                onClick={() => handleSort(c)}
              >
                {c}
                <SortIcon column={c} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr className='bg-default-100 font-semibold border-b-2 border-default-200'>
            <td className='py-2 px-3'>{averageRow.client}</td>
            {currencies.map((c) => (
              <td key={c} className='py-2 px-3 text-right'>
                {fmtRate(averageRow.rates[c])}
              </td>
            ))}
          </tr>
          {sortedClientRows.slice(0, visibleCount).map((row) => (
            <tr
              key={row.client}
              className='border-b border-default-100 hover:bg-default-50'
            >
              <td className='py-2 px-3 font-medium'>{row.client}</td>
              {currencies.map((c) => (
                <td key={c} className='py-2 px-3 text-right'>
                  {fmtRate(row.rates[c])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    {sortedClientRows.length > visibleCount && (
      <div className='flex justify-center mt-2'>
        <Button
          variant='light'
          size='sm'
          onPress={() =>
            setVisibleCount((c) =>
              Math.min(c + 10, sortedClientRows.length)
            )
          }
          className='w-full'
        >
          {t('Cargar más')} ({sortedClientRows.length - visibleCount}{' '}
          {t('restantes')})
        </Button>
      </div>
    )}
    </div>
  );
});

export default React.memo(ExchangeRatesTable);
